import React, { useState, useRef, useCallback } from 'react';
import dayjs from 'shared/helpers/dayjs';
import { useTranslation } from 'react-i18next';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { Arrow, Time } from 'icons/untitledui';
import useClickOutside from 'shared/hooks/useClickOutside';

import * as Styled from 'dashboard/styles/Filters.style';
import { calendarPopperSx } from 'dashboard/styles/Filters.style';

export default function DateSelector({
  selectedDate,
  setSelectedDate,
}: {
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
}): JSX.Element {
  const { t } = useTranslation('pageTeamLeadInsights');

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const muiPopperRef = useRef<HTMLDivElement>(null);

  const handleDateChange = useCallback(
    (date: Date | null) => {
      setSelectedDate(date);
      setDatePickerOpen(false);
    },
    [setSelectedDate]
  );

  const handleClose = useCallback(() => {
    setDatePickerOpen(false);
  }, []);

  const handleToggle = () => setDatePickerOpen((prev) => !prev);

  useClickOutside([anchorRef, muiPopperRef], handleClose, datePickerOpen);

  return (
    <Styled.FilterWrapper>
      <Styled.DateRangeButton
        ref={anchorRef}
        onClick={handleToggle}
        $open={datePickerOpen}
        aria-label={
          selectedDate
            ? dayjs(selectedDate).format('MMMM D, YYYY')
            : t('viewTeamLead.SelectNewDate')
        }
      >
        <span>
          {selectedDate
            ? dayjs(selectedDate).format('MMMM D, YYYY')
            : t('viewTeamLead.SelectNewDate')}
        </span>
        <Time.Calendar size={20} />
      </Styled.DateRangeButton>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DesktopDatePicker
          value={selectedDate}
          onChange={handleDateChange}
          maxDate={dayjs().endOf('day').toDate()}
          open={datePickerOpen}
          onClose={handleClose}
          slots={{
            leftArrowIcon: Arrow.ChevronLeft,
            rightArrowIcon: Arrow.ChevronRight,
          }}
          slotProps={{
            textField: {
              sx: {
                width: 0,
                height: 0,
                overflow: 'hidden',
                '& .MuiOutlinedInput-root': {
                  display: 'none',
                },
              },
            },
            popper: {
              ref: muiPopperRef,
              anchorEl: anchorRef.current,
              placement: 'bottom-end',
              sx: calendarPopperSx,
            },
          }}
        />
      </LocalizationProvider>
    </Styled.FilterWrapper>
  );
}
